import { Box, Button, Typography } from '@mui/material';
import { ZoomOutMap } from '@mui/icons-material';
import { useEffect } from 'react';
import type { ReadingsByStage } from '../../../types/directShearReading';
import type { DirectShearStage } from '../../../types/directShear';
import type { AnalysisData } from './analysisTypes';
import { SummaryResultsTable } from './SummaryResultsTable';
import { useMultiStageOverlayOptions } from './charts/MultiStageOverlayChart';
import { SyncedChart } from './SyncedChart';
import { useSyncedCharts } from './useSyncedCharts';

interface SummaryAnalysisProps {
  readingsByStage: ReadingsByStage;
  analysisByStage: Record<number, AnalysisData>;
  stages?: DirectShearStage[];
}

export const SummaryAnalysis = ({
  readingsByStage,
  analysisByStage,
  stages,
}: SummaryAnalysisProps) => {
  // Show plots as soon as any stage has processed data (readings exist),
  // NOT gated on whether ranges/highlights are set.
  const hasAnyData = Object.values(readingsByStage).some(
    (readings) => readings && readings.length > 0,
  );

  const sharedProps = { readingsByStage, analysisByStage };

  const stressOpts = useMultiStageOverlayOptions({
    ...sharedProps,
    yField: 'CorrectedShearStress',
    title: 'Corrected Shear Stress vs. Horizontal Displacement',
    yLabel: 'Shear Stress [kPa]',
  });
  const frictionOpts = useMultiStageOverlayOptions({
    ...sharedProps,
    yField: 'CorrectedFrictionAngle',
    title: 'Friction Angle vs. Horizontal Displacement',
    yLabel: 'Friction Angle [\u00B0]',
  });
  const dispOpts = useMultiStageOverlayOptions({
    ...sharedProps,
    yField: 'VerticalDisplacementCumulative',
    title: 'Vertical Displacement vs. Horizontal Displacement',
    yLabel: 'Vertical Displacement [mm]',
  });
  const dilOpts = useMultiStageOverlayOptions({
    ...sharedProps,
    yField: 'DilationAngle',
    title: 'Dilation Angle vs. Horizontal Displacement',
    yLabel: 'Dilation Angle [\u00B0]',
  });
  const stressPathOpts = useMultiStageOverlayOptions({
    ...sharedProps,
    xField: 'CorrectedNormalStress',
    yField: 'CorrectedShearStress',
    title: 'Shear Stress vs. Normal Stress (All Stages)',
    xLabel: 'Corrected Normal Stress [kPa]',
    yLabel: 'Corrected Shear Stress [kPa]',
    height: 600,
    // BUG-2026-0199 #4: no data sits at the origin on this stress-path chart, so
    // let the Y axis auto-scale to the data instead of pinning it to 0.
  });

  const { registerChart, syncCrosshair, hideCrosshairs, syncZoom, resetAllZoom } = useSyncedCharts();
  const chartProps = { onSyncCrosshair: syncCrosshair, onHideCrosshairs: hideCrosshairs, onSyncZoom: syncZoom, registerChart };

  // Reset zoom when summary tab mounts (prevents stale zoom from stage tabs)
  // Two-pass reset: immediate for fast renders, delayed for slow chart init
  useEffect(() => {
    const t1 = setTimeout(() => resetAllZoom(), 150);
    const t2 = setTimeout(() => resetAllZoom(), 500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [resetAllZoom]);

  if (!hasAnyData) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Process stage data on the Result Entry page first.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 1 }}>
      <SummaryResultsTable analysisByStage={analysisByStage} stages={stages} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ZoomOutMap sx={{ fontSize: 16 }} />}
          onClick={resetAllZoom}
          sx={{ textTransform: 'none', fontSize: '0.78rem' }}
        >
          Reset All Zoom
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
        <SyncedChart options={stressOpts} chartIndex={0} {...chartProps} />
        <SyncedChart options={frictionOpts} chartIndex={1} {...chartProps} />
        <SyncedChart options={dispOpts} chartIndex={2} {...chartProps} />
        <SyncedChart options={dilOpts} chartIndex={3} {...chartProps} />
        <SyncedChart options={stressPathOpts} chartIndex={4} {...chartProps} />
      </Box>
    </Box>
  );
};
